/**
 *
 * @module configurationManager
 * @see https://github.bamtech.co/sdk-doc/spec-sdk/blob/master/specs/feature_overviews/configuration.md
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';
import EmbeddedConfiguration from './embeddedConfiguration';

const Util = Services.Util;
const Storage = Services.Storage;
const BootstrapConfiguration = Services.Configuration.BootstrapConfiguration;
const EnvironmentConfiguration = Services.Configuration.EnvironmentConfiguration;
const SdkSessionConfiguration = Services.Configuration.SdkSessionConfiguration;
const ConfigurationClient = Services.Configuration.ConfigurationClient;
const DustLogUtility = Services.Internal.Dust.DustLogUtility;
const ConfigurationManagerDustUrnReference = Services.Internal.Dust.DustUrnReference.configuration.configurationManager;
const PlatformProfileReference = Services.Configuration.PlatformProfileReference;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class ConfigurationManager
 * @desc Manages configuration information required to use the SDK.
 *
 */
export default class ConfigurationManager {

    /**
     *
     * @param {BootstrapConfiguration} bootstrapConfiguration
     * @param {Object} packageData
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Services.Storage} [storage=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(bootstrapConfiguration, packageData, logger = Logging, storage = null) {

        const errorMessage = {
            ctor: 'ConfigurationManager(bootstrapConfiguration, packageData, logger, storage)',
            get bootstrapConfiguration() {
                return `${this.ctor} needs BootstrapConfiguration instance`;
            },
            get packageData() {
                return `${this.ctor} needs plain Object instance`;
            },
            get storage() {
                return `${this.ctor} needs Storage instance`;
            }
        };

        instanceStrict(bootstrapConfiguration, BootstrapConfiguration, errorMessage.bootstrapConfiguration, InvalidArgumentException); // eslint-disable-line
        nonEmptyObject(packageData, errorMessage.packageData, InvalidArgumentException);

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {BootstrapConfiguration}
         *
         */
        this.bootstrapConfiguration = bootstrapConfiguration;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(logger);

        /**
         *
         * @access public
         * @type {String}
         * @since 2.0.0
         * @example 3.1
         *
         */
        this.sdkVersion = packageData.sdkVersionShort;

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.EnvironmentConfiguration}
         *
         */
        this.environmentConfiguration = ConfigurationManager.createEnvironmentConfiguration(
            this.sdkVersion,
            this.bootstrapConfiguration
        );

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.ConfigurationClientConfiguration}
         *
         */
        this.configurationClientConfiguration = ConfigurationManager.getClientConfiguration();

        /**
         *
         * @access private
         * @type {SDK.Services.Configuration.ConfigurationClient}
         *
         */
        this.client = new ConfigurationClient(this.configurationClientConfiguration, this.logger);

        /**
         *
         * @access private
         * @type {String}
         * @desc cache key scoped under client ID and environment to prevent clashes,
         * maintains the same structure as all other cacheKey(s) in the SDK
         * @since 1.1.2
         *
         */
        this.cacheKey = `
            __bam_sdk_sdkSession_configuration--
            ${this.bootstrapConfiguration.clientId}_
            ${this.bootstrapConfiguration.environment}
        `.replace(/(?:\n\s+)/g, '');

        /**
         *
         * @access private
         * @type {Boolean}
         * @desc used to enable dust logging
         *
         */
        this.dustEnabled = Util.getSafe(() => this.client.dustEnabled);

        this.logger.log(ConfigurationManager.name, 'Created.');

    }

    /**
     *
     * @access public
     * @desc Retrieves the remote configuration, falls back to cached version if available.
     * @returns {Promise<SDK.Services.Configuration.SdkSessionConfiguration>}
     *
     */
    async getConfiguration() {

        const { client, cacheKey, dustEnabled, storage, logger } = this;
        const { bootstrapConfiguration, environmentConfiguration } = this;

        const dustLogUtility = dustEnabled && new DustLogUtility({
            logger,
            source: ConfigurationManager.name,
            urn: ConfigurationManagerDustUrnReference.getConfiguration
        });

        if (!Util.getSafe(() => bootstrapConfiguration.configHostName)) {

            const embeddedConfiguration = ConfigurationManager.loadEmbeddedConfiguration();

            bootstrapConfiguration.configHostName = embeddedConfiguration.defaultConfigHostName;

        }

        logger.info(ConfigurationManager.name, `Getting configuration:
            clientId: "${bootstrapConfiguration.clientId}",
            environment: "${bootstrapConfiguration.environment}",
            deviceFamily: "${environmentConfiguration.deviceFamily}",
            sdkVersion: "${environmentConfiguration.sdkVersion}",
            configVersion: "${environmentConfiguration.configVersion}",
            configHostName: "${bootstrapConfiguration.configHostName}"
        `.replace(/(\s(?=\s))/g, ''));

        try {

            const sdkSessionConfiguration = await client.getConfiguration(bootstrapConfiguration, environmentConfiguration); // eslint-disable-line

            logger.log(ConfigurationManager.name, 'Successfully fetched configuration.');

            await this.cacheConfiguration(sdkSessionConfiguration);

            return Promise.resolve(sdkSessionConfiguration);

        } catch (ex) {

            logger.error(ConfigurationManager.name, 'Unable to get configuration.');
            logger.log(ConfigurationManager.name, `Loading from storage, cache key: "${cacheKey}".`);

            if (dustEnabled) {
                dustLogUtility.captureErrors(ex);
            }

            try {

                const storedSdkSessionConfiguration = storage.get(cacheKey);
                const sdkSessionConfiguration = new SdkSessionConfiguration(storedSdkSessionConfiguration);

                return Promise.resolve(sdkSessionConfiguration);

            } catch (e) {

                logger.error(ConfigurationManager.name, 'Unable to fetch cached configuration.');

                return await this.getFallbackConfiguration();

            }

        } finally {

            if (dustEnabled) {
                dustLogUtility.log();
            }

        }

    }

    /* #region private: */

    /**
     *
     * @access private
     * @returns {Promise<Void>}
     *
     */
    async cacheConfiguration(sdkSessionConfiguration) {

        const { cacheKey, storage, logger } = this;

        logger.log(ConfigurationManager.name, `Adding configuration to Storage, cache key: "${cacheKey}".`);

        return await storage.set(cacheKey, sdkSessionConfiguration);

    }

    /**
     *
     * @access private
     * @returns {Promise<SDK.Services.Configuration.SdkSessionConfiguration>}
     *
     */
    async getFallbackConfiguration() {

        const isFallback = true;
        const fallbackConfiguration = new EmbeddedConfiguration(isFallback);
        const configurationClient = new ConfigurationClient(fallbackConfiguration.client);

        const { bootstrapConfiguration, environmentConfiguration, logger } = this;

        logger.log(ConfigurationManager.name, 'Fetching fallback configuration.');

        const config = await configurationClient.getConfiguration(bootstrapConfiguration, environmentConfiguration);

        return Promise.resolve(config);

    }

    /**
     *
     * @access private
     * @returns {SDK.Configuration.EmbeddedConfiguration}
     *
     */
    static loadEmbeddedConfiguration() {

        const isFallback = false;

        return new EmbeddedConfiguration(isFallback);

    }

    /**
     *
     * @access private
     * @param {String} sdkVersion
     * @param {SDK.Services.Configuration.BootstrapConfiguration} bootstrapConfiguration
     * @returns {SDK.Services.Configuration.EnvironmentConfiguration}
     *
     */
    static createEnvironmentConfiguration(sdkVersion, bootstrapConfiguration) {

        const platform = Util.getSafe(() => bootstrapConfiguration.platform);
        const platformProfile = PlatformProfileReference[platform];
        const applicationRuntime = Util.getSafe(() => platformProfile.applicationRuntime);
        const deviceProfile = Util.getSafe(() => platformProfile.deviceProfile);

        return new EnvironmentConfiguration({ sdkVersion, applicationRuntime, deviceProfile });

    }

    /**
     *
     * @access private
     * @returns {SDK.Services.Configuration.ConfigurationClientConfiguration}
     *
     */
    static getClientConfiguration() {

        const embeddedConfiguration = ConfigurationManager.loadEmbeddedConfiguration();

        return embeddedConfiguration.client;

    }

}



// WEBPACK FOOTER //
// ./src/configuration/configurationManager.js